import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const AdminApprovalPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    processApproval();
  }, []);

  const processApproval = async () => {
    const token = searchParams.get('token');
    
    if (!token) {
      setError('Token no proporcionado');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/admin-approval/${token}`);
      setResult(response.data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-lg text-gray-600">Procesando solicitud...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle size={24} />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">{error}</p>
            <Button
              onClick={() => navigate('/')}
              className="w-full"
              variant="outline"
            >
              Volver al Inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isApproved = result?.action === 'approved';
  const isRejected = result?.action === 'rejected';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isApproved && (
              <>
                <CheckCircle size={24} className="text-green-600" />
                <span className="text-green-600">Administrador Aprobado</span>
              </>
            )}
            {isRejected && (
              <>
                <XCircle size={24} className="text-red-600" />
                <span className="text-red-600">Solicitud Rechazada</span>
              </>
            )}
            {!isApproved && !isRejected && (
              <>
                <AlertCircle size={24} className="text-amber-600" />
                <span className="text-amber-600">Ya Procesado</span>
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700 mb-2">
              <strong>Estado:</strong> {result?.message}
            </p>
            {result?.admin_email && (
              <p className="text-gray-600 text-sm">
                <strong>Email:</strong> {result.admin_email}
              </p>
            )}
          </div>

          {isApproved && (
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <p className="text-green-800 text-sm">
                ✅ El administrador ha sido notificado por email y ya puede acceder al sistema.
              </p>
            </div>
          )}

          {isRejected && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <p className="text-red-800 text-sm">
                ❌ La solicitud ha sido rechazada.
              </p>
            </div>
          )}

          <Button
            onClick={() => navigate('/')}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            Volver al Inicio
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminApprovalPage;
